import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useGetBookRequestByIdQuery } from "../api/bookRequestApi";
import {
  BookRequestResponseType,
  BookRequestStatus,
  ApiBookRequest,
} from "../types/bookRequestTypes";
import BookRequestModals from "../BookRequestModals";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../../core/data/slice/authSlice";
import { useReviewBookRequestMutation } from "../api/bookRequestApi";
import { showToast } from "../../../../core/utils/alert";
import { all_routes } from "../../../router/all_routes";

// Helper function to transform API response to client format
const transformBookRequest = (
  apiRequest: ApiBookRequest
): BookRequestResponseType => {
  return {
    id: apiRequest.id,
    student_id: apiRequest.student.id,
    student_name: apiRequest.student.name || "",
    studentid: apiRequest.student.profile.studentid,
    center_id: apiRequest.center.id,
    center_name: apiRequest.center.name,
    course: apiRequest.course.name,
    stage: apiRequest.stage.name,
    request_date: apiRequest.requested_on,
    status: apiRequest.status,
    approved_date:
      apiRequest.status === "approved"
        ? apiRequest.reviewed_on || undefined
        : undefined,
    approved_by:
      apiRequest.status === "approved"
        ? apiRequest.reviewed_by !== null
          ? apiRequest.reviewed_by
          : undefined
        : undefined,
    rejection_reason:
      apiRequest.status === "rejected"
        ? apiRequest.reviewer_note || undefined
        : undefined,
    rejected_date:
      apiRequest.status === "rejected"
        ? apiRequest.reviewed_on || undefined
        : undefined,
    completion_date:
      apiRequest.status === "completed"
        ? apiRequest.reviewed_on || undefined
        : undefined,
  };
};

const ViewBookRequest: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<BookRequestResponseType | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [requestIdToDelete, setRequestIdToDelete] = useState<
    number | undefined
  >(undefined);
  const routes = all_routes;

  // Use RTK Query hook directly
  const { data: requestData, isLoading: isLoadingRequest } =
    useGetBookRequestByIdQuery({ id: id || "0" });

  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "superadmin";
  const isCenterAdmin = userRole === "centeradmin";
  const [reviewBookRequest] = useReviewBookRequestMutation();

  // Function to check payment status
  const checkPaymentStatus = (bookrequest: ApiBookRequest | undefined) => {
    if (!bookrequest) {
      return { isValid: false, message: "Invalid request data" };
    }

    const center_payments = bookrequest.student?.center_payments;
    const stageName = bookrequest.stage?.name;

    if (!center_payments || center_payments.length === 0) {
      if (stageName === "Stage 1") {
        return {
          isValid: false,
          message: "Registration fee share for this student is pending",
        };
      } else {
        return {
          isValid: false,
          message: `Course fee share for stage ${
            parseInt(stageName.split(" ")[1]) - 1
          } is pending for this student`,
        };
      }
    }

    if (stageName === "Stage 1") {
      // For Stage 1, only check registration fee
      const registrationFee = center_payments.find(
        (payment) =>
          payment.fee_type === "registrationfee" &&
          payment.status === "completed"
      );
      if (!registrationFee) {
        return {
          isValid: false,
          message: "Registration fee share for this student is pending",
        };
      }
    } else {
      // For other stages, check previous stage's course fee
      const stageId = bookrequest.stage?.id;
      const previousStageId = stageId ? stageId - 1 : 0;
      const previousStageCourseFee = center_payments.find(
        (payment) =>
          payment.fee_type === "coursefee" &&
          payment.status === "completed" &&
          payment.stage_id !== null &&
          payment.stage_id === previousStageId
      );
      if (!previousStageCourseFee) {
        return {
          isValid: false,
          message: `Course fee share for previous stage is pending`,
        };
      }
    }

    return { isValid: true, message: "" };
  };

  const handleApproveClick = () => {
    if (!requestData?.data?.bookrequest) {
      showToast({
        title: "Warning",
        text: "Invalid request data",
        icon: "warning",
      });
      return;
    }

    const paymentStatus = checkPaymentStatus(requestData.data.bookrequest);
    if (!paymentStatus.isValid) {
      showToast({
        title: "Warning",
        text: paymentStatus.message || "Payment verification failed",
        icon: "warning",
      });
      return;
    }

    setRequestIdToDelete(requestData.data.bookrequest.id);
  };

  useEffect(() => {
    if (!isLoadingRequest && requestData?.data?.bookrequest) {
      const bookRequest = requestData.data.bookrequest;
      setRequest(transformBookRequest(bookRequest));
    } else if (!isLoadingRequest && !requestData?.data?.bookrequest) {
      // Request not found, redirect to list
      navigate(routes.bookRequestList);
    }
  }, [isLoadingRequest, requestData, navigate]);

  // Function to render status badge with appropriate color and icon
  const renderStatusBadge = (status: BookRequestStatus) => {
    let colorClass = "";

    switch (status) {
      case "pending":
        colorClass = "warning";
        break;
      case "approved":
        colorClass = "success";
        break;
      case "rejected":
        colorClass = "danger";
        break;
      case "completed":
        colorClass = "info";
        break;
      default:
        colorClass = "secondary";
    }

    return (
      <span
        className={`badge badge-soft-${colorClass} d-inline-flex align-items-center`}
      >
        <i className="ti ti-circle-filled fs-5 me-1"></i>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (isLoadingRequest) {
    return (
      <div className="page-wrapper">
        <div className="content container">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading book request details...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="page-wrapper">
        <div className="content container">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="ti ti-alert-circle text-warning fs-1 mb-3"></i>
                  <h4>Book Request Not Found</h4>
                  <p>
                    The book request you're looking for doesn't exist or has
                    been removed.
                  </p>
                  <Link
                    to={routes.bookRequestList}
                    className="btn btn-primary mt-3"
                  >
                    <i className="ti ti-arrow-left me-2"></i>
                    Back to Book Requests List
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content container">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Book Request Details</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.centerDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.bookRequestList}>Book Requests</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Book Request Details
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <Link
                  to={routes.bookRequestList}
                  className="btn btn-outline-primary d-flex align-items-center"
                >
                  <i className="ti ti-arrow-left me-2"></i>
                  Back
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Main Content - Student and Request Details */}
            <div className="col-md-8 mb-4">
              <div className="card">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-book fs-16"></i>
                    </span>
                    <h4 className="text-dark mb-0">Book Request Details</h4>
                  </div>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      {renderStatusBadge(request.status)}
                      <span className="text-muted">|</span>
                      <span className="text-muted">
                        Requested on: {request.request_date}
                      </span>
                    </div>
                  </div>

                  {/* Add alert for pending status */}
                  {request.status === "pending" && (
                    <div className="alert alert-warning mb-3" role="alert">
                      <div className="d-flex">
                        <i className="ti ti-alert-triangle mt-2 me-2"></i>
                        <div>
                          <h6 className="alert-heading mb-1">
                            Pending Request
                          </h6>
                          <p className="mb-0">
                            {isSuperAdmin
                              ? "This book request is pending your review. You can approve or reject this request."
                              : "This book request is pending approval from the administrator. Please wait for the review."}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Add alert for approved status */}
                  {request.status === "approved" && (
                    <div className="alert alert-success mb-3" role="alert">
                      <div className="d-flex">
                        <i className="ti ti-check mt-2 me-2"></i>
                        <div>
                          <h6 className="alert-heading mb-1">
                            Approved Request
                          </h6>
                          <p className="mb-0">
                            This book request has been approved on{" "}
                            {request.approved_date}.
                            {isCenterAdmin &&
                              " The books can now be delivered to the student."}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Add alert for rejected status */}
                  {request.status === "rejected" && (
                    <div className="alert alert-danger mb-3" role="alert">
                      <div className="d-flex">
                        <i className="ti ti-x mt-2 me-2"></i>
                        <div>
                          <h6 className="alert-heading mb-1">
                            Rejected Request
                          </h6>
                          <p className="mb-0">
                            This book request was rejected on{" "}
                            {request.rejected_date}. Reason:{" "}
                            {request.rejection_reason || "No reason provided"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Add alert for completed status */}
                  {request.status === "completed" && (
                    <div className="alert alert-info mb-3" role="alert">
                      <div className="d-flex">
                        <i className="ti ti-circle-check mt-2 me-2"></i>
                        <div>
                          <h6 className="alert-heading mb-1">
                            Completed Request
                          </h6>
                          <p className="mb-0">
                            This book request has been completed and books have
                            been delivered to the student on{" "}
                            {request.completion_date}.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Student Information */}
                  <div className="mb-4">
                    <h5 className="border-bottom pb-2 mb-3">
                      Student Information
                    </h5>
                    <div className="row mb-3">
                      <div className="col-sm-4">
                        <p className="text-muted mb-0">Student ID:</p>
                      </div>
                      <div className="col-sm-8">
                        <p className="mb-0 fw-medium">{request.studentid}</p>
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-sm-4">
                        <p className="text-muted mb-0">Student Name:</p>
                      </div>
                      <div className="col-sm-8">
                        <p className="mb-0 fw-medium">{request.student_name}</p>
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-sm-4">
                        <p className="text-muted mb-0">Course:</p>
                      </div>
                      <div className="col-sm-8">
                        <p className="mb-0 fw-medium">{request.course}</p>
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-sm-4">
                        <p className="text-muted mb-0">Requested Stage:</p>
                      </div>
                      <div className="col-sm-8">
                        <p className="mb-0 fw-medium">{request.stage}</p>
                      </div>
                    </div>
                    {isSuperAdmin && (
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="text-muted mb-0">Center:</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="mb-0 fw-medium">
                            {request.center_name}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Request Info - Sidebar */}
            <div className="col-md-4 mb-4">
              <div className="card h-100">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-info-circle fs-16"></i>
                    </span>
                    <h4 className="text-dark mb-0">Request Status</h4>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <p className="text-muted mb-0">Status:</p>
                    </div>
                    <div className="col-sm-8">
                      {renderStatusBadge(request.status)}
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <p className="text-muted mb-0">Requested on:</p>
                    </div>
                    <div className="col-sm-8">
                      <p className="mb-0 fw-medium">{request.request_date}</p>
                    </div>
                  </div>

                  {request.status === "approved" && (
                    <>
                      <div className="row mb-3">
                        <div className="col-sm-4">
                          <p className="text-muted mb-0">Approved on:</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="mb-0 fw-medium">
                            {request.approved_date}
                          </p>
                        </div>
                      </div>
                      {/* <div className="row mb-3">
                        <div className="col-sm-4">
                          <p className="text-muted mb-0">By:</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="mb-0 fw-medium">
                            {request.approved_by}
                          </p>
                        </div>
                      </div> */}
                    </>
                  )}

                  {request.status === "rejected" && (
                    <>
                      <div className="row mb-3">
                        <div className="col-sm-4">
                          <p className="text-muted mb-0">Rejected on:</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="mb-0 fw-medium">
                            {request.rejected_date}
                          </p>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="text-muted mb-0">Reason:</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="mb-0 fw-medium">
                            {request.rejection_reason}
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  {request.status === "completed" && (
                    <div className="row">
                      <div className="col-sm-4">
                        <p className="text-muted mb-0">Completed on:</p>
                      </div>
                      <div className="col-sm-8">
                        <p className="mb-0 fw-medium">
                          {request.completion_date}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Request Summary and Actions - in a row at the bottom */}
            <div className="row">
              {/* Request Summary */}
              <div className="col-md-8 mb-4">
                <div className="card h-100">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-list-details fs-16"></i>
                      </span>
                      <h4 className="text-dark mb-0">Request Summary</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th>Student</th>
                            <th>Course</th>
                            <th>Requested Stage</th>
                            <th>Request Date</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              {request.student_name} ({request.studentid})
                            </td>
                            <td>{request.course}</td>
                            <td>{request.stage}</td>
                            <td>{request.request_date}</td>
                            <td>{renderStatusBadge(request.status)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions Card - only show if actions are available */}
              {(isCenterAdmin && request.status === "approved") ||
              (isSuperAdmin && request.status === "pending") ||
              (isCenterAdmin && request.status === "pending") ? (
                <div className="col-md-4 mb-4">
                  <div className="card h-100">
                    <div className="card-header bg-light">
                      <div className="d-flex align-items-center">
                        <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                          <i className="ti ti-settings fs-16"></i>
                        </span>
                        <h4 className="text-dark mb-0">Actions</h4>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="d-flex flex-column gap-3">
                        {/* Show Mark as Delivered only for centeradmin */}
                        {isCenterAdmin && request.status === "approved" && (
                          <button
                            className="btn btn-outline-info"
                            data-bs-toggle="modal"
                            data-bs-target="#complete-modal"
                            onClick={() => setRequestIdToDelete(request.id)}
                          >
                            <i className="feather-check-square me-2"></i> Mark
                            as Completed
                          </button>
                        )}

                        {/* Show Approve/Reject for superadmin */}
                        {isSuperAdmin && request.status === "pending" && (
                          <>
                            {/* Payment Warning */}
                            {requestData?.data?.bookrequest &&
                              !checkPaymentStatus(requestData.data.bookrequest)
                                .isValid && (
                                <div
                                  className="alert alert-warning mb-3"
                                  role="alert"
                                >
                                  <div className="d-flex">
                                    <i className="ti ti-alert-triangle mt-2 me-2"></i>
                                    <div>
                                      <h6 className="alert-heading mb-1">
                                        Payment Required
                                      </h6>
                                      <p className="mb-0">
                                        {checkPaymentStatus(
                                          requestData.data.bookrequest
                                        ).message ||
                                          "Payment verification failed"}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}

                            <button
                              className="btn btn-success"
                              data-bs-toggle="modal"
                              data-bs-target="#approve-modal"
                              onClick={handleApproveClick}
                              disabled={
                                !checkPaymentStatus(
                                  requestData?.data?.bookrequest
                                ).isValid
                              }
                            >
                              <i className="ti ti-check me-2"></i> Approve
                              Request
                            </button>

                            <button
                              className="btn btn-outline-danger"
                              data-bs-toggle="modal"
                              data-bs-target="#reject-modal"
                              onClick={() => {
                                setRequestIdToDelete(request.id);
                                setRejectReason("");
                              }}
                            >
                              <i className="ti ti-x me-2"></i> Reject Request
                            </button>
                          </>
                        )}

                        {/* Show Delete button only for centeradmin and pending status */}
                        {isCenterAdmin && request.status === "pending" && (
                          <button
                            className="btn btn-outline-danger"
                            data-bs-toggle="modal"
                            data-bs-target="#delete-modal"
                            onClick={() => setRequestIdToDelete(request.id)}
                          >
                            <i className="ti ti-trash me-2"></i> Delete Request
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Add the BookRequestModals component */}
      <BookRequestModals
        requestId={requestIdToDelete}
        onSuccess={() => {
          navigate(-1);
        }}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
      />
    </>
  );
};

export default ViewBookRequest;
